import {
  AbstractControl,
  AsyncValidatorFn,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { keys, some as _some } from 'lodash';

import { first, map, Observable, of, switchMap, take, timer } from 'rxjs';

export function requiredIf(predicate: () => boolean): ValidatorFn {
  return (formControl) => {
    if (!formControl.parent) {
      return null;
    }
    if (predicate()) {
      return Validators.required(formControl);
    }
    return null;
  };
}

export function requiredFromList<E>(
  collection: E[],
  predicateFactory: (controlValue: unknown, iteratorValue: E) => boolean = (
    v1,
    v2
  ) => v1 === v2
): ValidatorFn {
  return (formControl) => {
    if (!formControl.parent || !collection) {
      return null;
    }

    const predicate = (v: E) => predicateFactory(formControl.value, v);

    return !_some(collection, (v) => predicate(v))
      ? { requiredFromList: true }
      : null;
  };
}

export function asyncRequiredFromList<E>(
  collection: Observable<E[]>,
  predicateFactory: (controlValue: unknown, iteratorValue: E) => boolean = (
    v1,
    v2
  ) => v1 === v2
): AsyncValidatorFn {
  return (formControl) => {
    if (!formControl.parent || !collection) {
      return of(null);
    }
    return collection.pipe(
      map((collection) => {
        const predicate = (v: E) => predicateFactory(formControl.value, v);
        return !_some(collection, (v) => predicate(v))
          ? { requiredFromList: true }
          : null;
      }),
      take(1)
    );
  };
}

export class IdValidator {
  static createValidator<T>(
    observableFactory: (id: T) => Observable<boolean>
  ): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      if (!control.value) {
        return of(null);
      }

      return observableFactory(control.value).pipe(
        map((result: boolean) => (result ? { idExists: true } : null))
      );
    };
  }
}

export function booleanValidatorFactory<T>(
  observableFactory: (value: T) => Observable<boolean>,
  name: string = 'boolean',
  options: { debounce: number } = { debounce: 500 }
): AsyncValidatorFn {
  return (control: AbstractControl): Observable<ValidationErrors | null> => {
    if (!control.value) {
      return of(null);
    }

    return timer(options.debounce).pipe(
      switchMap(() =>
        observableFactory(control.value).pipe(
          first(),
          map((result: boolean) => (result ? { [name]: true } : null))
        )
      )
    );
  };
}

export function matchValidator(
  matchTo: string,
  reverse?: boolean
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (control.parent && reverse) {
      const c = (control.parent?.controls as any)[matchTo] as AbstractControl;
      if (c) {
        c.updateValueAndValidity();
      }
      return null;
    }
    return !!control.parent &&
      !!control.parent.value &&
      control.value === (control.parent?.controls as any)[matchTo].value
      ? null
      : { matching: true };
  };
}

export class LAValidators {
  static lowercaseChars(n: number): ValidatorFn {
    return (control: AbstractControl) => {
      const regExp = new RegExp(`^(?=.*[a-z].{${n - 1},}).*$`);

      return regExp.test(control.value) && control.value
        ? null
        : { lowercaseChars: { n } };
    };
  }

  static uppercaseChars(n: number): ValidatorFn {
    return (control: AbstractControl) => {
      const regExp = new RegExp(`^(?=.*[A-Z].{${n - 1},}).*$`);

      return regExp.test(control.value) && control.value
        ? null
        : { uppercaseChars: { n } };
    };
  }

  static numberChars(n: number): ValidatorFn {
    return (control: AbstractControl) => {
      const regExp = new RegExp(`^(?=.*[0-9].{${n - 1},}).*$`);

      return regExp.test(control.value) && control.value
        ? null
        : { numberChars: { n } };
    };
  }

  static specialChars(n: number): ValidatorFn {
    return (control: AbstractControl) => {
      const regExp = new RegExp(`^(?=.*[£_\\-=!@#$%^&*].{${n - 1},}).*$`);

      return regExp.test(control.value) && control.value
        ? null
        : { specialChars: { n } };
    };
  }

  static noSpaces(): ValidatorFn {
    return (control: AbstractControl) => {
      const regExp = /\s/;

      return !regExp.test(control.value) && control.value
        ? null
        : { noSpaces: true };
    };
  }

  static onlyPasswordChars(): ValidatorFn {
    return (control: AbstractControl) => {
      const regExp = /^[0-9a-zA-Z£_\-=!@#$%^&*]*$/;

      return regExp.test(control.value) && control.value
        ? null
        : { onlyPasswordChars: true };
    };
  }

  static passwordRequirements(): ValidatorFn[] {
    return [
      Validators.minLength(12),
      this.uppercaseChars(1),
      this.numberChars(1),
      this.lowercaseChars(1),
      this.specialChars(1),
      this.onlyPasswordChars(),
    ];
  }

  static some(validator: ValidatorFn, ids?: string[]): ValidatorFn {
    return (control) => {
      ids = ids || keys((control as FormGroup).controls);

      return _some(
        ids,
        (id: string) => !validator((control as FormGroup).controls[id])
      )
        ? null
        : { some: true };
    };
  }
}
